/**
 * Typed selectors for the polymorphic `surfaceTool` slot.
 *
 * The store keeps a single discriminated `SurfaceToolState | null` field;
 * these helpers narrow on `kind` so callers (handlers, gizmos, tests, the
 * alignment grid) can read tool-specific drafts/stages without re-typing
 * the `?? state.surfaceTool?.kind === "X" ? ... : null` boilerplate at
 * every read site.
 */
import type {
  AppState,
  SurfaceFootprintDraftState,
  SurfaceFootprintStage,
  SurfaceInsetDraftState,
  SurfaceInsetStage,
  SurfacePolyDraftState,
  SurfacePolyStage,
  SurfaceToolKind,
  SurfaceToolState,
} from "./storeTypes.ts";

type SurfaceToolPick = Pick<AppState, "surfaceTool">;

/** Return the tool slot iff its `kind` matches; otherwise `null`. */
export function selectSurfaceToolOfKind<K extends SurfaceToolKind>(
  state: SurfaceToolPick,
  kind: K,
): Extract<SurfaceToolState, { kind: K }> | null {
  const tool = state.surfaceTool;
  if (!tool || tool.kind !== kind) return null;
  return tool as Extract<SurfaceToolState, { kind: K }>;
}

/** Active surface tool kind, or `null` when no surface tool is armed. */
export function selectSurfaceToolKind(
  state: SurfaceToolPick,
): SurfaceToolKind | null {
  return state.surfaceTool?.kind ?? null;
}

/**
 * True iff *any* surface tool currently owns a canvas drag (orbit
 * suppression). Mirrors the historical `surfaceXActive` boolean reads
 * scattered across `Viewport3D`.
 */
export function selectAnySurfaceToolActive(state: SurfaceToolPick): boolean {
  return state.surfaceTool?.active === true;
}

// ---------------------------------------------------------------------------
// Per-kind narrowing helpers
// ---------------------------------------------------------------------------

export function selectSurfaceBoxDraft(
  state: SurfaceToolPick,
): SurfaceFootprintDraftState | null {
  return selectSurfaceToolOfKind(state, "box")?.draft ?? null;
}

export function selectSurfaceBoxStage(
  state: SurfaceToolPick,
): SurfaceFootprintStage | null {
  return selectSurfaceToolOfKind(state, "box")?.stage ?? null;
}

export function selectSurfaceCylinderDraft(
  state: SurfaceToolPick,
): SurfaceFootprintDraftState | null {
  return selectSurfaceToolOfKind(state, "cylinder")?.draft ?? null;
}

export function selectSurfaceCylinderStage(
  state: SurfaceToolPick,
): SurfaceFootprintStage | null {
  return selectSurfaceToolOfKind(state, "cylinder")?.stage ?? null;
}

export function selectSurfacePolyDraft(
  state: SurfaceToolPick,
): SurfacePolyDraftState | null {
  return selectSurfaceToolOfKind(state, "poly")?.draft ?? null;
}

export function selectSurfacePolyStage(
  state: SurfaceToolPick,
): SurfacePolyStage | null {
  return selectSurfaceToolOfKind(state, "poly")?.stage ?? null;
}

export function selectSurfaceInsetDraft(
  state: SurfaceToolPick,
): SurfaceInsetDraftState | null {
  return selectSurfaceToolOfKind(state, "inset")?.draft ?? null;
}

export function selectSurfaceInsetStage(
  state: SurfaceToolPick,
): SurfaceInsetStage | null {
  return selectSurfaceToolOfKind(state, "inset")?.stage ?? null;
}

// ---------------------------------------------------------------------------
// Mutation helpers — keep handler/gizmo call sites readable
// ---------------------------------------------------------------------------

/**
 * Patch the current surface tool's fields if its kind matches; no-op
 * otherwise. Use this when a callback fires after a tool may have been
 * disarmed (e.g. async snap result, ref pointer-up after blur).
 *
 * For typed `draft` patches the `kind` argument narrows the union — pass
 * the literal kind (`"box"`, `"poly"`, …) so TypeScript checks the patch
 * shape against the correct draft type.
 */
export function patchSurfaceTool<K extends SurfaceToolKind>(
  current: SurfaceToolState | null,
  kind: K,
  patch: Partial<Extract<SurfaceToolState, { kind: K }>>,
): SurfaceToolState | null {
  if (!current || current.kind !== kind) return current;
  return { ...current, ...patch } as SurfaceToolState;
}
